// src/components/LPStaking/LPStakingInterface.js

import React, { useState, useContext } from 'react';
import { LPStakingContext } from '../../context/LPStakingContext';
import { WalletContext } from '../../context/WalletContext';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Tooltip,
  Modal
} from '@mui/material';
import { ethers } from 'ethers';
import { NETWORK_CONFIG } from '../../contracts/constants';
import dayjs from 'dayjs';

const LPStakingInterface = () => {
  const { account, chainId } = useContext(WalletContext);
  const {
    isInitialized: isLPStakingInitialized,
    isInitializing,
    lpStakingPositions,
    totalLPStaked,
    userLPNFTs,
    loadingStake,
    loadingUnstake,
    loadingClaim,
    isTransactionPending,
    userBoostPercentage,
    stakeLP,
    unstakeLP,
    claimRewards,
    refreshData
  } = useContext(LPStakingContext);

  const [selectedNFT, setSelectedNFT] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [expandedPositionId, setExpandedPositionId] = useState(null);
  const [openLiquidityTips, setOpenLiquidityTips] = useState(false);

  const isCorrectNetwork = Number(chainId) === NETWORK_CONFIG.chainId;
  const isLoading =
    !isLPStakingInitialized ||
    loadingStake ||
    loadingUnstake ||
    loadingClaim ||
    isTransactionPending;

  const stakingPeriods = [
    { id: 0, name: '1 Month', reward: '5%' },
    { id: 1, name: '3 Months', reward: '15%' },
    { id: 2, name: '6 Months', reward: '32%' },
    { id: 3, name: '1 Year', reward: '65%' },
  ];

  const formatLiquidity = (liquidityWei) => {
    if (!liquidityWei) return '0';
    try {
      const liquidity = parseFloat(ethers.utils.formatEther(liquidityWei));
      if (liquidity >= 1e12) return (liquidity / 1e12).toFixed(2) + 'T';
      if (liquidity >= 1e9) return (liquidity / 1e9).toFixed(2) + 'B';
      if (liquidity >= 1e6) return (liquidity / 1e6).toFixed(2) + 'M';
      if (liquidity >= 1e3) return (liquidity / 1e3).toFixed(2) + 'K';
      return liquidity.toFixed(2);
    } catch {
      return '0';
    }
  };

  const handleStake = async () => {
    if (!selectedNFT) return;
    try {
      await stakeLP(selectedNFT, selectedPeriod);
      setSelectedNFT('');
      await refreshData();
    } catch {}
  };

  const handleUnstake = async (positionId) => {
    try {
      await unstakeLP(positionId);
      await refreshData();
    } catch {}
  };

  const handleClaimRewards = async (positionId) => {
    try {
      await claimRewards(positionId);
      await refreshData();
    } catch {}
  };

  const toggleExpandPosition = (positionId) => {
    setExpandedPositionId(expandedPositionId === positionId ? null : positionId);
  };

  if (isInitializing) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Initializing LP Staking...
        </Typography>
      </Box>
    );
  }

  if (!isCorrectNetwork) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Please connect to {NETWORK_CONFIG.name} to use LP Staking
        </Typography>
      </Box>
    );
  }

  if (!isLPStakingInitialized) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">
          LP Staking is not yet available on {NETWORK_CONFIG.name}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          The LP Staking feature is coming soon. Please check back later.
        </Typography>
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Please connect your wallet to use LP Staking</Typography>
      </Box>
    );
  }

  return (
    <Box className="lp-staking-container" sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            color: 'var(--primary-color)',
            textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 1,
          }}
        >
          LP Staking
        </Typography>
        {/* Removed <text> and <small>, replaced with a recognized element (Typography) */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontStyle: 'italic',
            color: 'grey',
            fontSize: '0.8rem',
            mb: 1,
          }}
        >
          LP Staking is only available for Unichain $CASH
        </Typography>
        <Tooltip title="Click to see tips on adding liquidity" arrow>
          <Typography
            variant="subtitle1"
            onClick={() => setOpenLiquidityTips(true)}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'var(--primary-color)',
              fontSize: { xs: '0.8rem', md: '1rem' },
            }}
          >
            How to add liquidity
          </Typography>
        </Tooltip>
      </Box>

      <Modal open={openLiquidityTips} onClose={() => setOpenLiquidityTips(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '400px' },
            bgcolor: 'var(--background-light)',
            border: '2px solid var(--primary-color)',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            wordWrap: 'break-word',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'var(--primary-color)' }}>
            Tips for Adding Liquidity
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
            1. Visit{' '}
            <a
              href="https://app.uniswap.org/#/add/liquidity"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}
            >
              Uniswap
            </a>
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
            2. Connect your wallet.
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
            3. Choose the pool for CASH and ETH.
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
            4. Use our CASH token address:
            <Box component="span" sx={{ fontWeight: 'bold', display: 'block', wordBreak: 'break-all' }}>
              0xA57ED06Ca426Fee4eBB748Eda7df5814e14990C8
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
            5. Add liquidity by depositing both CASH and ETH.
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff' }}>
            6. Receive LP tokens for staking.
          </Typography>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={() => setOpenLiquidityTips(false)}
              sx={{ bgcolor: 'var(--primary-color)', color: 'var(--secondary-color)' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: { xs: 1, md: 2 },
              bgcolor: '#1c1c1c',
              color: 'var(--primary-color)',
              border: '1px solid rgba(255, 204, 0, 0.3)',
            }}
          >
            <Typography variant="subtitle1" sx={{ borderBottom: '1px solid rgba(255, 204, 0, 0.3)', pb: 0.5, mb: 1 }}>
              Total Liquidity Staked
            </Typography>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              {formatLiquidity(totalLPStaked)} LP
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: { xs: 1, md: 2 },
              bgcolor: '#1c1c1c',
              color: 'var(--primary-color)',
              border: '1px solid rgba(255, 204, 0, 0.3)',
            }}
          >
            <Typography variant="subtitle1" sx={{ borderBottom: '1px solid rgba(255, 204, 0, 0.3)', pb: 0.5, mb: 1 }}>
              Your LP NFTs Available
            </Typography>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              {userLPNFTs.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: { xs: 1, md: 2 },
              bgcolor: '#1c1c1c',
              color: 'var(--primary-color)',
              border: '1px solid rgba(255, 204, 0, 0.3)',
            }}
          >
            <Typography variant="subtitle1" sx={{ borderBottom: '1px solid rgba(255, 204, 0, 0.3)', pb: 0.5, mb: 1 }}>
              Booster NFT Bonus:
            </Typography>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              +{userBoostPercentage}%
            </Typography>
            {userBoostPercentage === 0 && (
              <Typography variant="caption" sx={{ textAlign: 'center', mt: 0.5 }}>
                Mint Booster NFTs to boost rewards!
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          bgcolor: '#1c1c1c',
          color: 'var(--primary-color)',
          border: '1px solid rgba(255, 204, 0, 0.3)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid rgba(255, 204, 0, 0.3)', pb: 0.5 }}>
          Stake LP NFT
        </Typography>
        {userLPNFTs && userLPNFTs.length > 0 ? (
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="nft-select-label" sx={{ color: 'rgba(255, 204, 0, 0.7)' }}>
                  Select LP NFT
                </InputLabel>
                <Select
                  labelId="nft-select-label"
                  value={selectedNFT}
                  onChange={(e) => setSelectedNFT(e.target.value)}
                  label="Select LP NFT"
                  disabled={isLoading}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: '#ffcc00', opacity: 0.7 }}>Select LP NFT</Typography>;
                    }
                    const nft = userLPNFTs.find((n) => n.tokenId === selected);
                    if (!nft) return selected;
                    return `LP NFT #${nft.tokenId} - ${formatLiquidity(nft.liquidity)}`;
                  }}
                  sx={{
                    color: '#ffcc00',
                    backgroundColor: 'rgba(28, 28, 28, 0.8)',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 204, 0, 0.5)',
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 204, 0, 0.8)',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ffcc00',
                      borderWidth: '2px',
                    },
                  }}
                >
                  {userLPNFTs.map((nft) => (
                    <MenuItem key={nft.tokenId} value={nft.tokenId}>
                      LP NFT #{nft.tokenId} - {formatLiquidity(nft.liquidity)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="period-select-label" sx={{ color: 'rgba(255, 204, 0, 0.7)' }}>
                  Staking Period
                </InputLabel>
                <Select
                  labelId="period-select-label"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="Staking Period"
                  disabled={isLoading}
                  displayEmpty
                  renderValue={(selected) => {
                    const period = stakingPeriods[selected];
                    if (!period) return 'Select period';
                    return `${period.name} - ${period.reward} Reward`;
                  }}
                  sx={{
                    color: '#ffcc00',
                    backgroundColor: 'rgba(28, 28, 28, 0.8)',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 204, 0, 0.5)',
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 204, 0, 0.8)',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ffcc00',
                      borderWidth: '2px',
                    },
                  }}
                >
                  {stakingPeriods.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.name} - {period.reward} Reward
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleStake}
                disabled={isLoading || !selectedNFT}
                sx={{
                  bgcolor: '#ffcc00',
                  color: '#1c1c1c',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#e6a90f',
                    color: '#1c1c1c',
                    border: '1px solid #ffcc00',
                    transform: 'scale(1.02)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 204, 0, 0.3)',
                    color: 'rgba(28, 28, 28, 0.7)',
                  },
                }}
              >
                {loadingStake ? <CircularProgress size={24} color="inherit" /> : 'Stake LP NFT'}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" textAlign="center" sx={{ py: 2 }}>
            You don't have any LP NFTs available to stake
          </Typography>
        )}
      </Paper>

      <Typography variant="h6" sx={{ mb: 1, color: '#ffcc00', textAlign: 'center' }}>
        My Staked Positions
      </Typography>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <CircularProgress sx={{ color: '#ffcc00' }} />
        </Box>
      ) : lpStakingPositions && lpStakingPositions.length > 0 ? (
        <Box component="ul" sx={{ listStyleType: 'none', p: 0 }}>
          {lpStakingPositions.map((position) => {
            const isExpired = dayjs().isAfter(dayjs.unix(position.stakingEndTimestamp));
            const durationLabel = stakingPeriods[position.stakingPeriod]?.name || 'Unknown';
            const liquidityStr = formatLiquidity(position.liquidity);
            const endDate = dayjs.unix(position.stakingEndTimestamp).format('DD/MM/YYYY');
            return (
              <Box key={position.positionId} component="li" sx={{ mb: 1 }}>
                <Button
                  onClick={() => toggleExpandPosition(position.positionId)}
                  disabled={isLoading}
                  sx={{
                    justifyContent: 'flex-start',
                    p: 1,
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: '#1c1c1c',
                    color: '#ffcc00',
                    border: '1px solid rgba(255, 204, 0, 0.3)',
                    borderRadius: 1,
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#ffcc00',
                      color: '#1c1c1c',
                      border: '1px solid #ffcc00',
                      transform: 'scale(1.02)',
                      opacity: 1,
                    },
                  }}
                >
                  {expandedPositionId === position.positionId ? '−' : '+'}
                  {` LP NFT #${position.tokenId} - ${liquidityStr} - ${durationLabel}`}
                </Button>
                <Collapse in={expandedPositionId === position.positionId} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      p: 1,
                      border: '1px solid rgba(255, 204, 0, 0.3)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      bgcolor: '#1a1a1a',
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="#ffcc00" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Liquidity:</strong> {liquidityStr}
                        </Typography>
                        <Typography variant="caption" color="#ffcc00" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Period:</strong> {durationLabel}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="#ffcc00" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Unlock:</strong> {endDate}
                        </Typography>
                        <Typography variant="caption" color="#ffcc00" sx={{ display: 'block' }}>
                          <strong>Status:</strong> {isExpired ? 'Expired' : 'Active'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {isExpired && (
                            <Button
                              onClick={() => handleClaimRewards(position.positionId)}
                              variant="contained"
                              disabled={isLoading}
                              sx={{
                                bgcolor: '#ffcc00',
                                color: '#1c1c1c',
                                fontSize: { xs: '0.65rem', md: '0.75rem' },
                                '&:hover': { bgcolor: '#e6a90f', color: '#1c1c1c' },
                              }}
                            >
                              {loadingClaim ? <CircularProgress size={20} color="inherit" /> : 'Claim & Unstake'}
                            </Button>
                          )}
                          <Button
                            onClick={() => handleUnstake(position.positionId)}
                            variant="outlined"
                            disabled={isLoading}
                            sx={{
                              borderColor: 'rgba(255, 204, 0, 0.5)',
                              color: '#ffcc00',
                              fontSize: { xs: '0.65rem', md: '0.75rem' },
                              '&:hover': {
                                borderColor: '#ffcc00',
                                bgcolor: 'rgba(255, 204, 0, 0.1)',
                                color: '#ffcc00',
                              },
                            }}
                          >
                            {loadingUnstake ? <CircularProgress size={20} color="inherit" /> : 'Unstake'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography
          variant="body2"
          textAlign="center"
          sx={{
            py: 1,
            color: '#ffcc00',
            bgcolor: '#1c1c1c',
            borderRadius: 1,
            border: '1px solid rgba(255, 204, 0, 0.3)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
          }}
        >
          You don't have any active staking positions
        </Typography>
      )}
    </Box>
  );
};

export default LPStakingInterface;